import React, { useContext, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Form, Field } from 'react-final-form'
import {
  Button,
  InputNumber,
  Input as AntInput,
  DatePicker as AntDatePicker,
  Modal as AntModal,
  Switch,
  Tooltip,
} from 'antd'
import styled from 'styled-components'
import moment from 'moment'
import useAction from 'utils/useAction'
import Modal from 'components/Modal/Modal'
import {
  selectors as surpriseBoxSelectors,
  actions as surpriseBoxActions,
} from 'domains/surpriseBox'
import {
  STATUSES,
  ACTIVE_STATUSES,
  TERMINAL_STATUSES,
  CANCELLABLE_STATUSES,
  SURPRISE_BOX_DATE_TIME_FORMAT,
} from '../constants'
import { getFieldLock } from '../mutability'

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Label = styled.label`
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);

  &.required::after {
    content: ' *';
    color: #ff4d4f;
  }
`

const InlineFieldsRow = styled.div`
  display: flex;
  gap: 16px;

  > div {
    flex: 1;
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
`

const RightButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`

const ErrorText = styled.span`
  color: #ff4d4f;
  font-size: 12px;
`

const HintText = styled.span`
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
`

const NameRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;

  > div:first-child {
    flex: 1;
  }
`

const StatusToggle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 4px;
`

const required = (value) =>
  value === undefined || value === null || value === '' ? 'Required' : undefined

const validatePositive = (min) => (value) => {
  if (value === undefined || value === null || value === '') return undefined
  if (Number(value) < min) return `Must be ≥ ${min}`
  return undefined
}

const makeValidate = ({ originalCloseTime, originalDrawTime, drawExtendOnly }) => (
  values
) => {
  const errors = {}
  const start = values.promotion_start_time
  const close = values.sales_close_time
  const draw = values.draw_time

  if (start && close && !moment(close).isAfter(moment(start))) {
    errors.sales_close_time = 'Must be after start time'
  }
  if (close && draw && moment(close).isAfter(moment(draw))) {
    errors.draw_time = 'Prize draw must be on or after sales close'
  }
  if (
    originalCloseTime &&
    close &&
    moment(close).isBefore(moment(originalCloseTime))
  ) {
    errors.sales_close_time = 'Can only be extended, not shortened'
  }
  if (
    drawExtendOnly &&
    originalDrawTime &&
    draw &&
    moment(draw).isBefore(moment(originalDrawTime))
  ) {
    errors.draw_time = 'Can only be extended once tickets are sold'
  }
  return errors
}

const TextInputField = ({ input, meta, label, isRequired, lock, maxLength }) => {
  const node = (
    <AntInput
      {...input}
      maxLength={maxLength}
      disabled={lock.locked}
      status={meta.touched && meta.error ? 'error' : undefined}
    />
  )
  return (
    <FormField>
      <Label className={isRequired ? 'required' : ''}>{label}</Label>
      {lock.locked ? (
        <Tooltip title={lock.reason} placement="topLeft">
          <span>{node}</span>
        </Tooltip>
      ) : (
        node
      )}
      {meta.touched && meta.error && <ErrorText>{meta.error}</ErrorText>}
    </FormField>
  )
}

const NumberField = ({ input, meta, label, isRequired, min, step, lock }) => {
  const node = (
    <InputNumber
      {...input}
      min={min}
      step={step}
      disabled={lock.locked}
      style={{ width: '100%' }}
      status={meta.touched && meta.error ? 'error' : undefined}
    />
  )
  return (
    <FormField>
      <Label className={isRequired ? 'required' : ''}>{label}</Label>
      {lock.locked ? (
        <Tooltip title={lock.reason} placement="topLeft">
          <span style={{ display: 'block' }}>{node}</span>
        </Tooltip>
      ) : (
        node
      )}
      {meta.touched && meta.error && <ErrorText>{meta.error}</ErrorText>}
    </FormField>
  )
}

const DateTimeField = ({
  input,
  meta,
  label,
  isRequired,
  lock,
  minDate,
}) => {
  const disabledDate = minDate
    ? (current) => current && current.isBefore(moment(minDate).startOf('day'))
    : undefined
  const node = (
    <AntDatePicker
      format={SURPRISE_BOX_DATE_TIME_FORMAT}
      showTime={{ format: 'HH:mm:ss' }}
      value={input.value ? moment(input.value) : null}
      onChange={input.onChange}
      disabled={lock.locked}
      disabledDate={disabledDate}
      style={{ width: '100%' }}
      status={meta.touched && meta.error ? 'error' : undefined}
      renderExtraFooter={() => `UTC${moment().format('Z')} time zone`}
    />
  )
  return (
    <FormField>
      <Label className={isRequired ? 'required' : ''}>{label}</Label>
      {lock.locked ? (
        <Tooltip title={lock.reason} placement="topLeft">
          <span style={{ display: 'block' }}>{node}</span>
        </Tooltip>
      ) : (
        node
      )}
      {!lock.locked && lock.extendOnly && (
        <HintText>{lock.reason}</HintText>
      )}
      {meta.touched && meta.error && <ErrorText>{meta.error}</ErrorText>}
    </FormField>
  )
}

const toIsoLocal = (value) => {
  if (!value) return null
  return moment(value).utc().format('YYYY-MM-DDTHH:mm:ss')
}

const FIELD_NAMES = [
  'name',
  'ticket_price',
  'prize_value',
  'promotion_start_time',
  'sales_close_time',
  'draw_time',
]

const PromotionForm = () => {
  const modal = useContext(Modal.Context)
  const promotion = useSelector(surpriseBoxSelectors.getCurrentPromotion)
  const isSubmitting = useSelector(surpriseBoxSelectors.getIsSubmitting)
  const fetchPromotion = useAction(surpriseBoxActions.fetchPromotion)
  const setCurrentPromotion = useAction(surpriseBoxActions.setCurrentPromotion)
  const createPromotion = useAction(surpriseBoxActions.createPromotion)
  const updatePromotion = useAction(surpriseBoxActions.updatePromotion)
  const activatePromotion = useAction(surpriseBoxActions.activatePromotion)
  const deactivatePromotion = useAction(surpriseBoxActions.deactivatePromotion)
  const cancelPromotion = useAction(surpriseBoxActions.cancelPromotion)

  const editingId = modal.data?.id

  useEffect(() => {
    if (editingId) {
      fetchPromotion({ id: editingId })
    } else {
      setCurrentPromotion(null)
    }
    return () => setCurrentPromotion(null)
  }, [editingId]) // eslint-disable-line react-hooks/exhaustive-deps

  const isEdit = Boolean(editingId)
  const status = promotion?.status
  const isTerminal = isEdit && TERMINAL_STATUSES.includes(status)
  const isActive = isEdit && ACTIVE_STATUSES.includes(status)
  const canCancel = isEdit && CANCELLABLE_STATUSES.includes(status)

  const showActivateSwitch =
    isEdit &&
    promotion &&
    (status === STATUSES.DRAFT || status === STATUSES.ACTIVE_NO_TICKETS_SOLD)

  const lockFor = (field) => (isEdit ? getFieldLock(field, status) : getFieldLock(field, null))

  const drawLock = lockFor('draw_time')
  const closeLock = lockFor('sales_close_time')

  const originalCloseTime = useMemo(
    () =>
      promotion?.sales_close_time
        ? moment.utc(promotion.sales_close_time).local()
        : null,
    [promotion]
  )
  const originalDrawTime = useMemo(
    () =>
      promotion?.draw_time
        ? moment.utc(promotion.draw_time).local()
        : null,
    [promotion]
  )

  const initialValues = useMemo(() => {
    if (!isEdit) return {}
    if (!promotion) return null
    return {
      name: promotion.name,
      ticket_price: promotion.ticket_price,
      prize_value: promotion.prize_value,
      promotion_start_time: promotion.promotion_start_time
        ? moment.utc(promotion.promotion_start_time).local()
        : null,
      sales_close_time: originalCloseTime,
      draw_time: originalDrawTime,
    }
  }, [promotion, isEdit, originalCloseTime, originalDrawTime])

  if (isEdit && !promotion) {
    return <div>Loading…</div>
  }

  const buildFullPayload = (values) => ({
    name: values.name,
    ticket_price: values.ticket_price,
    prize_value: values.prize_value,
    promotion_start_time: toIsoLocal(values.promotion_start_time),
    sales_close_time: toIsoLocal(values.sales_close_time),
    draw_time: toIsoLocal(values.draw_time),
  })

  const buildDirtyPayload = (values, dirtyFields) => {
    const full = buildFullPayload(values)
    const dirty = {}
    FIELD_NAMES.forEach((f) => {
      if (dirtyFields[f]) dirty[f] = full[f]
    })
    return dirty
  }

  const doSubmit = (values, form) => {
    const callback = () => modal.close()
    if (!isEdit) {
      createPromotion({ values: buildFullPayload(values), callback })
      return
    }
    const dirtyFields = form.getState().dirtyFields || {}
    const payload = buildDirtyPayload(values, dirtyFields)
    if (Object.keys(payload).length === 0) {
      modal.close()
      return
    }
    updatePromotion({ id: editingId, values: payload, callback })
  }

  const onSubmit = (values, form) => {
    if (isActive) {
      AntModal.confirm({
        title: 'Edit a live campaign?',
        content:
          'Buyers may already be in the funnel — changes apply immediately.',
        okText: 'Save changes',
        cancelText: 'Back',
        onOk: () => doSubmit(values, form),
      })
      return
    }
    doSubmit(values, form)
  }

  const handleCancelClick = () => {
    AntModal.confirm({
      title: 'Cancel promotion',
      content: `Cancel "${promotion.name}"? This cannot be undone. The campaign will be archived and no further actions will be possible.`,
      okText: 'Cancel promotion',
      okType: 'danger',
      cancelText: 'Back',
      onOk: () =>
        cancelPromotion({ id: editingId, callback: () => modal.close() }),
    })
  }

  const drawExtendOnly = drawLock.extendOnly
  const validate = makeValidate({
    originalCloseTime,
    originalDrawTime,
    drawExtendOnly,
  })

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues || {}}
      validate={validate}
      render={({ handleSubmit, pristine }) => (
        <StyledForm onSubmit={handleSubmit}>
          <NameRow>
            <Field name="name" validate={required}>
              {(props) => (
                <TextInputField
                  {...props}
                  label="Name"
                  isRequired
                  maxLength={255}
                  lock={lockFor('name')}
                />
              )}
            </Field>
            {showActivateSwitch && (
              <StatusToggle>
                <Label>Active</Label>
                <Switch
                  checked={ACTIVE_STATUSES.includes(status)}
                  loading={isSubmitting}
                  onChange={(checked) => {
                    if (checked) {
                      AntModal.confirm({
                        title: 'Activate promotion',
                        content: `Activate "${promotion.name}"? Only one promotion can be active at a time.`,
                        okText: 'Activate',
                        onOk() {
                          activatePromotion({ id: editingId })
                        },
                      })
                    } else {
                      AntModal.confirm({
                        title: 'Deactivate promotion',
                        content: `Move "${promotion.name}" back to DRAFT? Deactivation is only allowed before any tickets are sold.`,
                        okText: 'Deactivate',
                        onOk() {
                          deactivatePromotion({ id: editingId })
                        },
                      })
                    }
                  }}
                />
              </StatusToggle>
            )}
          </NameRow>

          <InlineFieldsRow>
            <Field name="ticket_price" validate={(v) => required(v) || validatePositive(0.01)(v)}>
              {(props) => (
                <NumberField
                  {...props}
                  label="Ticket Price (USD)"
                  isRequired
                  min={0.01}
                  step={0.01}
                  lock={lockFor('ticket_price')}
                />
              )}
            </Field>

            <Field name="prize_value" validate={(v) => required(v) || validatePositive(0)(v)}>
              {(props) => (
                <NumberField
                  {...props}
                  label="Prize Value (USD)"
                  isRequired
                  min={0}
                  step={0.01}
                  lock={lockFor('prize_value')}
                />
              )}
            </Field>
          </InlineFieldsRow>

          <Field name="promotion_start_time" validate={required}>
            {(props) => (
              <DateTimeField
                {...props}
                label="Promotion Start Time (UTC)"
                isRequired
                lock={lockFor('promotion_start_time')}
              />
            )}
          </Field>

          <InlineFieldsRow>
            <Field name="sales_close_time" validate={required}>
              {(props) => (
                <DateTimeField
                  {...props}
                  label="Sales Close Time (UTC)"
                  isRequired
                  lock={closeLock}
                  minDate={closeLock.extendOnly ? originalCloseTime : null}
                />
              )}
            </Field>

            <Field name="draw_time" validate={required}>
              {(props) => (
                <DateTimeField
                  {...props}
                  label="Prize Draw Time (UTC)"
                  isRequired
                  lock={drawLock}
                  minDate={drawLock.extendOnly ? originalDrawTime : null}
                />
              )}
            </Field>
          </InlineFieldsRow>

          <ButtonsWrapper>
            {canCancel ? (
              <Button danger onClick={handleCancelClick} disabled={isSubmitting}>
                Cancel promotion
              </Button>
            ) : (
              <span />
            )}
            <RightButtons>
              <Button onClick={() => modal.close()}>Close</Button>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={pristine || isTerminal}
              >
                {isEdit ? 'Save' : 'Create'}
              </Button>
            </RightButtons>
          </ButtonsWrapper>
        </StyledForm>
      )}
    />
  )
}

export default PromotionForm
